/**
 * Montos y reglas de pago para /r/cumple.
 * AR → MercadoPago (ARS). Exterior → Stripe (USD, enteros redondeados).
 */

import { getServerTemplateConfig, normalizeWorkerCountry } from "@/lib/serverTemplateDetection";

export type CumpleAmountTier = {
  value: number;
  emoji: string;
  label: string;
};

export const CUMPLE_AMOUNTS_ARS: readonly CumpleAmountTier[] = [
  { value: 34999, emoji: "🎈", label: "Saludo" },
  { value: 49999, emoji: "🎁", label: "Regalito" },
  { value: 69999, emoji: "🎂", label: "Torta" },
  { value: 99000, emoji: "🎉", label: "Festejo" },
  { value: 149999, emoji: "🎊", label: "Celebración" },
  { value: 199999, emoji: "👑", label: "Cumpleañera" },
];

/** USD redondeados (paridad con tiers AR del funnel sorprendeme / save-message MIN 35). */
export const CUMPLE_AMOUNTS_USD: readonly CumpleAmountTier[] = [
  { value: 35, emoji: "🎈", label: "Greeting" },
  { value: 50, emoji: "🎁", label: "Gift" },
  { value: 70, emoji: "🎂", label: "Cake" },
  { value: 99, emoji: "🎉", label: "Party" },
  { value: 150, emoji: "🎊", label: "Celebration" },
  { value: 200, emoji: "👑", label: "Birthday" },
];

export const CUMPLE_MIN_ARS = 34999;
export const CUMPLE_MIN_USD = 35;

/** Monto libre > este valor (ARS) → copy de video de regalo (front only). */
export const CUMPLE_GIFT_VIDEO_CUSTOM_THRESHOLD_ARS = 149999;
/** Paridad USD con tier Celebración / umbral AR. */
export const CUMPLE_GIFT_VIDEO_CUSTOM_THRESHOLD_USD = 150;

export function getCumpleTopTierValues(market: CumpleMarket): number[] {
  return market.amounts.slice(-2).map((tier) => tier.value);
}

/**
 * Video de regalo en modo agradecimiento — solo front, sin backend.
 * AR: los 2 tiers más altos (149999, 199999) o monto libre > 149999.
 * USD: los 2 tiers más altos (150, 200) o monto libre > 150.
 */
export function cumpleQualifiesForGiftVideo(
  market: CumpleMarket,
  selectedAmount: string,
  customAmount: string,
): boolean {
  const topTwo = getCumpleTopTierValues(market);
  const customThreshold =
    market.currency === "USD"
      ? CUMPLE_GIFT_VIDEO_CUSTOM_THRESHOLD_USD
      : CUMPLE_GIFT_VIDEO_CUSTOM_THRESHOLD_ARS;

  const customTrimmed = customAmount.trim();
  if (customTrimmed) {
    const customValue = normalizeCumpleChargeAmount(parseFloat(customTrimmed), market);
    if (customValue > customThreshold) return true;
  }

  const selectedTrimmed = selectedAmount.trim();
  if (selectedTrimmed && !customTrimmed) {
    const selectedValue = parseFloat(selectedTrimmed);
    return Number.isFinite(selectedValue) && topTwo.includes(selectedValue);
  }

  return false;
}

export type CumpleMarket = {
  isArgentina: boolean;
  country: string;
  currency: "ARS" | "USD";
  gateway: "mercadopago" | "stripe";
  language: "es" | "en";
  amounts: readonly CumpleAmountTier[];
  minAmount: number;
};

/**
 * País efectivo para gateway de pago en /r/cumple.
 * Prioriza cfCountry (geo Cloudflare) sobre cookies/config stale.
 * Override `?country=XX` solo en dev/debug (2 letras ISO).
 */
export function resolveCumplePaymentCountry(countryOverride?: string | null): string {
  if (countryOverride?.trim().length === 2) {
    return normalizeWorkerCountry(countryOverride);
  }
  const cfg = getServerTemplateConfig();
  const cf = normalizeWorkerCountry(cfg?.cfCountry);
  if (cf !== "UNKNOWN") return cf;
  return normalizeWorkerCountry(cfg?.country);
}

export function resolveCumpleMarket(countryOverride?: string | null): CumpleMarket {
  const cfg = getServerTemplateConfig();
  const country = resolveCumplePaymentCountry(countryOverride);
  const isArgentina = country === "AR";
  const langBase = (cfg?.language ?? (isArgentina ? "es-AR" : "en-US")).split("-")[0]?.toLowerCase();

  return {
    isArgentina,
    country,
    currency: isArgentina ? "ARS" : "USD",
    gateway: isArgentina ? "mercadopago" : "stripe",
    language: isArgentina || langBase === "es" ? "es" : "en",
    amounts: isArgentina ? CUMPLE_AMOUNTS_ARS : CUMPLE_AMOUNTS_USD,
    minAmount: isArgentina ? CUMPLE_MIN_ARS : CUMPLE_MIN_USD,
  };
}

/** Monto final en unidades mayores (ARS o USD entero). */
export function normalizeCumpleChargeAmount(raw: number, market: CumpleMarket): number {
  if (!Number.isFinite(raw) || raw <= 0) return 0;
  return market.currency === "USD" ? Math.round(raw) : Math.round(raw);
}

export function formatCumpleAmount(amount: number, market: CumpleMarket): string {
  if (market.currency === "USD") {
    return `$${amount} USD`;
  }
  return `$${amount.toLocaleString("es-AR")} ARS`;
}

/** Versión corta para CTAs mobile (sin sufijo de moneda). */
export function formatCumpleAmountCompact(amount: number, market: CumpleMarket): string {
  if (market.currency === "USD") {
    return `$${amount}`;
  }
  return `$${amount.toLocaleString("es-AR")}`;
}

/** Tier preestablecido que coincide con el monto (si el fan eligió un botón del grid). */
export function findCumpleAmountTier(
  amount: number,
  market: CumpleMarket,
): CumpleAmountTier | undefined {
  const normalized = normalizeCumpleChargeAmount(amount, market);
  return market.amounts.find((tier) => tier.value === normalized);
}

/** Valores permitidos del grid (para validación server-side de regalos cumple). */
export function getCumplePresetAmounts(market: CumpleMarket): number[] {
  return market.amounts.map((tier) => tier.value);
}